import { FormControl, FormHelperText, FormLabel, RadioGroup, RadioGroupProps } from "@mui/material";
import { Control, Controller, FieldValues, Path, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

type FormRadioGroupProps<IFormValues extends FieldValues = FieldValues> = Omit<RadioGroupProps, 'value'> & {
    name: Path<IFormValues>,
    control?: Control<IFormValues, any>,
    shouldUnregister?: boolean,
    label?: string,
    readOnly?: boolean,
    required?: boolean,
    children: React.ReactNode
}

const FormRadioGroup = <IFormValues extends FieldValues = FieldValues>({
    name,
    control,
    shouldUnregister,
    label,
    readOnly = false,
    required = false,
    children,
    ...props
}: FormRadioGroupProps<IFormValues>) => {

    const { t } = useTranslation('booking');

    const formContext = useFormContext<IFormValues>();

    return (
        <Controller
            name={name}
            control={control ?? formContext.control}
            shouldUnregister={shouldUnregister}
            render={({ field, fieldState, formState }) =>
                <FormControl error={!!fieldState.error} required={required}>
                    {label && <FormLabel>{label}</FormLabel>}
                    <RadioGroup
                        ref={field.ref}
                        {...props}
                        value={field.value}
                        onChange={(e, value) => {
                            if (!readOnly && !formState.isSubmitting) {
                                field.onChange(value);
                                props.onChange?.(e, value);
                            }
                        }}
                        onBlur={field.onBlur}
                        row={props.row ?? true}
                    >
                        {children}
                    </RadioGroup>
                    {fieldState.error?.message && <FormHelperText>{t(fieldState.error.message)}</FormHelperText>}
                </FormControl>
            }
        />
    )
};

export default FormRadioGroup;